import React, { useState, useCallback, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useOutletContext } from 'react-router-dom';
import { Button, Col, Row, Skeleton, Empty, message } from 'antd';
import type { BackendRedeemItem, RedeemApiResponse } from 'types/dto/redeem';
import OrderStatusCell from './subviews/OrderStatusCell';
import CouponAndVoucherCell from 'pages/wallet/couponsAndVouchers/subviews/CouponAndVoucherCell';
import LandingSection from './subviews/LandingSection';
import MemberShipSection from 'pages/profile/subviews/membershipSection/MemberShipSection';
import RedeemApply from 'pages/redeem/subviews/redeemApply/RedeemApply';
import './Landing.scss';
import { HistoryOrderEntity } from 'types/dto/order';
import {
  buildCouponsAndVouchersPath,
  buildOrderHistoryDetailPath,
  buildOrderHistoryListPath,
  buildProfilePath,
  buildRedeemPath,
} from 'utils/pathBuilder';
import { fetchRedeemItems } from 'services/redeemService';
import dayjs from 'dayjs';
import { fetchOrderHistory } from 'services/historyService';
import { MAX_ITEM_COUNT_IN_LANDING_PAGE, SLASH_FORMAT } from 'constants/consts';
import { TFunction } from 'i18next';
import { BackendLoyaltyItem } from 'types/dto/wallet';
import { fetchLoyaltyItems } from 'services/walletService';
import { fetchTier } from 'services/profileService';
import { MemberProfile, MembershipTier } from 'types/dto/profile';
import { useSession } from 'context/SessionContext';

// Redeem Card Component with Image Loading
interface RedeemCardProps {
  item: BackendRedeemItem;
  onClick: (item: BackendRedeemItem) => void;
  t: TFunction<[string, string, string], undefined>;
}

interface BackendHistoryOrderItem extends Record<string, unknown> {
  ophId: HistoryOrderEntity['oph_id'];
  ophSts: HistoryOrderEntity['oph_sts'];
  stsNam: HistoryOrderEntity['sts_nam'];
  ophRegNum: HistoryOrderEntity['oph_regnum'];
  ophColStrDtm: HistoryOrderEntity['oph_colstrdtm'];
  ophColEndDtm: HistoryOrderEntity['oph_colenddtm'];
  ophDelStrDtm: HistoryOrderEntity['oph_delstrdtm'];
  ophDelEndDtm: HistoryOrderEntity['oph_delenddtm'];
  glusCount: HistoryOrderEntity['glus_count'];
  ophRmkToCus: HistoryOrderEntity['oph_rmktocus'];
}

const RedeemCard: React.FC<RedeemCardProps> = ({ item, onClick, t }) => {
  const { i18n } = useTranslation();
  const isEnglish = i18n.language === 'en';
  const [imageLoading, setImageLoading] = useState(true);

  return (
    <div className="landing-redeem-card" onClick={() => onClick(item)}>
      {/* {item.isNew && (
        <div className="landing-new-badge">
          <Tag className="landing-new-badge-tag">
            {t('new', { ns: 'landing' })}
          </Tag>
        </div>
      )} */}
      <div className="landing-redeem-image">
        {imageLoading && (
          <Skeleton.Image active={true} className="image-skeleton" />
        )}
        <img
          src={isEnglish ? item.image.en : item.image.zh}
          alt={typeof item.title === 'string' ? item.title : 'Redeem Item'}
          onLoad={() => setImageLoading(false)}
          onError={() => setImageLoading(false)}
          style={{ display: imageLoading ? 'none' : 'block' }}
        />
      </div>
      <div className="landing-redeem-info">
        <div className="landing-redeem-title">
          {isEnglish ? item.title.en : item.title.zh}
        </div>
        <div>{t('nowUntil') + dayjs().format(SLASH_FORMAT)}</div>
      </div>
    </div>
  );
};

const Landing: React.FC = () => {
  const { t, i18n } = useTranslation(['landing', 'profile', 'wallet']);
  const { t: tCommon } = useTranslation('common');

  const lang = i18n.language;
  const navigate = useNavigate();
  const currentLanguage = i18n.language;

  const [redeemVisible, setRedeemVisible] = useState(false);
  const [selectedRedeemItem, setSelectedRedeemItem] =
    useState<BackendRedeemItem>();
  const [redeemItems, setRedeemItems] = useState<BackendRedeemItem[]>([]);
  const [activeCoupons, setActiveCoupons] = useState<BackendLoyaltyItem[]>([]);
  const [activeCouponsTotal, setActiveCouponsTotal] = useState<BackendLoyaltyItem[]>([]);
  const [tierData, setTierData] = useState<MembershipTier>();
  const [historyOrders, setHistoryOrders] = useState<HistoryOrderEntity[]>([]);

  //set default to true to fix UI flash issue
  const [isLoading, setIsLoading] = useState(true);
  const [tierLoadError, setTierLoadError] = useState(false);
  const [redeemLoadError, setRedeemLoadError] = useState(false);
  const [historyLoadError, setHistoryLoadError] = useState(false);
  const [couponLoadError, setCouponLoadError] = useState(false);
  const { profile } = useOutletContext<{ profile: MemberProfile | null }>();

  const {session} = useSession();

  const handleRedeemCardClick = useCallback((item: BackendRedeemItem) => {
    setSelectedRedeemItem(item);
    setRedeemVisible(true);
  }, []);

  const handleRedeemClose = useCallback(() => {
    setRedeemVisible(false);
    setSelectedRedeemItem(undefined);
  }, []);

  const handleOrderViewDetails = useCallback(
    (order: HistoryOrderEntity) => {
      navigate(buildOrderHistoryDetailPath(String(order.oph_id)));
    },
    [navigate]
  );

  // Render Redeem Item Card
  const renderRedeemCard = (item: BackendRedeemItem) => (
    <RedeemCard
      key={item.id}
      item={item}
      onClick={handleRedeemCardClick}
      t={t}
    />
  );

  const handleRedeemSuccess = useCallback(async () => {
    setRedeemVisible(false);
    setSelectedRedeemItem(undefined);

    try {
      //1. Fetch redeem items
      const redeemItemsResponse: RedeemApiResponse = await fetchRedeemItems();
      setRedeemItems(redeemItemsResponse.results ?? []);

      //2. Fetch tier data
      const tierData = await fetchTier('/profile');
      setTierData(tierData);
    } catch (err) {
      message.error(tCommon('error.loadDataError'));
    }
  }, [session, tCommon]);

  useEffect(() => {
    const initData = async () => {
      try {
        setIsLoading(true);
        setTierLoadError(false);
        setRedeemLoadError(false);
        setHistoryLoadError(false);
        setCouponLoadError(false);

        await Promise.all([
          (async () => {
            try {
              //1. Fetch tier data
              const tierData = await fetchTier('/profile');
              setTierData(tierData);
            } catch (error) {
              console.error('Failed to load tier data:', error);
              setTierData(undefined);
              setTierLoadError(true);
            }
          })(),
          (async () => {
            try {
              //2. Fetch redeem items
              const redeemItemsResponse: RedeemApiResponse = await fetchRedeemItems();
              setRedeemItems(redeemItemsResponse.results ?? []);
            } catch (error) {
              console.error('Failed to load redeem items:', error);
              setRedeemItems([]);
              setRedeemLoadError(true);
            }
          })(),
          (async () => {
            try {
              //3. Fetch order history
              const response = await fetchOrderHistory(
                'history',
                lang,
                profile?.loyalty_account?.unique_id || '',
              );

              const mappedOrders: HistoryOrderEntity[] = (response || []).map(
                (item: BackendHistoryOrderItem) => ({
                  ...item,
                  oph_id: item.ophId,
                  oph_sts: item.ophSts,
                  sts_nam: item.stsNam,
                  oph_regnum: item.ophRegNum,
                  oph_colstrdtm: item.ophColStrDtm,
                  oph_colenddtm: item.ophColEndDtm,
                  oph_delstrdtm: item.ophDelStrDtm,
                  oph_delenddtm: item.ophDelEndDtm,
                  glus_count: item.glusCount,
                  oph_rmktocus: item.ophRmkToCus,
                }),
              );

              const oneYearAgo = new Date();
              oneYearAgo.setFullYear(oneYearAgo.getFullYear() - 1);

              const filteredOrders = mappedOrders.filter((order) => {
                if (!order.oph_colstrdtm) return false;
                const orderDate = new Date(order.oph_colstrdtm);
                if (isNaN(orderDate.getTime())) return false;
                return orderDate >= oneYearAgo;
              });

              setHistoryOrders(filteredOrders);
            } catch (error) {
              console.error('Failed to load order history:', error);
              setHistoryOrders([]);
              setHistoryLoadError(true);
            }
          })(),
          (async () => {
            try {
              //4. Fetch coupons
              const couponsResponse = await fetchLoyaltyItems();
              setActiveCoupons(couponsResponse.data.results);
              setActiveCouponsTotal(couponsResponse.data.total);
            } catch (error) {
              console.error('Failed to load coupons:', error);
              setActiveCoupons([]);
              setCouponLoadError(true);
            }
          })(),
        ]);
      } catch (error) {
        console.error('Unexpected landing page loading error:', error);
      } finally {
        setIsLoading(false);
      }
    };
    initData();
  }, [currentLanguage, lang, session]);

  return (
    <div className="landing-container">
      {/* Show loading skeleton overlay while data is loading */}
      {isLoading && (
        <div className="landing-content">
          <Skeleton active paragraph={{ rows: 4 }} />
        </div>
      )}

      {/* Landing content - always mounted but hidden when loading */}
      {!isLoading && (
        <div className="landing-content">
          <Row gutter={[16, 16]}>
            <Col xs={24} sm={12} md={8}>
              {/* Profile Section */}
              <div className="landing-profile-section">
                <div className="landing-section-header">
                  <div className="landing-section-title">
                    {t('profile', { ns: 'landing' })}
                  </div>
                  <Button
                    type="link"
                    onClick={() => navigate(buildProfilePath())}
                  >
                    {t('details', { ns: 'landing' })}
                  </Button>
                </div>
                <div className="landing-profile-section-content">
                  {tierLoadError ? (
                    <div className="load-data-error">
                      {tCommon('error.loadDataError')}
                    </div>
                  ) : (
                    tierData && (
                      <MemberShipSection
                        membershipData={tierData}
                        isVertical={true}
                      />
                    )
                  )}
                </div>
              </div>
            </Col>

            {/* Redeem Section */}
            <Col xs={24} sm={12} md={16}>
              <LandingSection
                title={t('redeem', { ns: 'landing' })}
                onHeaderClick={() => navigate(buildRedeemPath())}
                className="redeem-section"
              >
                {redeemLoadError ? (
                  <div className="load-data-error">
                    {tCommon('error.loadDataError')}
                  </div>
                ) : redeemItems.length > 0 ? (
                  redeemItems
                    .slice(0, MAX_ITEM_COUNT_IN_LANDING_PAGE)
                    .map(renderRedeemCard)
                ) : (
                  <div className="landing-no-items">
                    <Empty
                      description={t('noRedeemItems', { ns: 'wallet' })}
                      className="empty-state"
                      image={Empty.PRESENTED_IMAGE_SIMPLE}
                    />
                  </div>
                )}
              </LandingSection>
            </Col>
          </Row>

          {/* Coupons Section */}
          <LandingSection
            title={`${t('myCouponsAndVouchers', { ns: 'landing' })} (${
               activeCouponsTotal
            })`}
            onHeaderClick={() => navigate(buildCouponsAndVouchersPath())}
          >
            {couponLoadError ? (
              <div className="load-data-error">
                {tCommon('error.loadDataError')}
              </div>
            ) : (
              activeCoupons
                .slice(0, MAX_ITEM_COUNT_IN_LANDING_PAGE)
                .map((coupon) => (
                  <CouponAndVoucherCell
                    key={coupon.id}
                    coupon={coupon}
                    isExpiredOrUsed={false}
                    className="landing-coupon-cell"
                    needUseButton={true}
                  />
                ))
            )}
          </LandingSection>

          {/* Pick up order statuses Section */}
          <LandingSection
            title={`${t('pickupOrderStatuses', { ns: 'landing' })} (${
              historyOrders?.length
            })`}
            onHeaderClick={() => navigate(buildOrderHistoryListPath())}
          >
            {historyLoadError ? (
              <div className="load-data-error">
                {tCommon('error.loadDataError')}
              </div>
            ) : (
              historyOrders
                ?.slice(0, historyOrders?.length)
                .map((order: HistoryOrderEntity) => (
                  <OrderStatusCell
                    key={order.oph_id}
                    order={order}
                    onViewDetails={handleOrderViewDetails}
                  />
                )) ?? null
            )}
          </LandingSection>
        </div>
      )}

      {/* Redeem Apply Modal */}
      <RedeemApply
        visible={redeemVisible}
        onClose={handleRedeemClose}
        onRedeemSuccess={handleRedeemSuccess}
        item={selectedRedeemItem}
        point={tierData?.progress?.current ?? 0}
      />
    </div>
  );
};

export default Landing;
